import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

import {
  PDF_COLORS,
  PDF_COMPANY,
  formatGeneratedAt,
  formatRp,
} from "@/lib/pdf";
import logo from "@/logo.png";

import {
  RIDER_PAYROLL_PAYMENT_METHOD_LABELS,
  RIDER_PAYROLL_STATUS_LABELS,
} from "../rider-payroll-labels";
import {
  formatDateShortSlash,
  formatDateSlashes,
  formatPeriodLong,
  formatPeriodShort,
} from "./format";

import type {
  PayrollAdjustment,
  RiderPayroll,
  RiderSale,
} from "@artisancode/api-types";

// Logo image slot left empty until the asset is provided (see dashed box placeholder).
const COMPANY_NAME = PDF_COMPANY.name;
const COMPANY_WEBSITE = "coffee.jamindonesia.com";
const COMPANY_ADDRESS = PDF_COMPANY.address;

const ORANGE = PDF_COLORS.orange;
const DARK = PDF_COLORS.dark;
const GRAY = PDF_COLORS.gray;

const CATEGORY_LABELS: Record<string, string> = {
  beverage: "Minuman",
  bakery: "Roti",
};

const styles = StyleSheet.create({
  page: { padding: 36, fontSize: 9, fontFamily: "Helvetica", color: DARK },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 10,
  },
  logoBox: {
    width: 70,
    height: 70,
    alignItems: "center",
    justifyContent: "center",
  },
  logoImage: { width: 70, height: 70, objectFit: "contain" },
  companyBlock: { alignItems: "flex-end" },
  companyName: { fontFamily: "Times-Bold", fontSize: 15, color: ORANGE },
  companyLine: { fontSize: 8, color: DARK, marginTop: 1 },
  companyLineItalic: {
    fontSize: 7,
    fontFamily: "Times-Italic",
    color: DARK,
    textAlign: "right",
  },
  divider: {
    borderBottomWidth: 2,
    borderBottomColor: ORANGE,
    marginBottom: 18,
  },
  docTitle: { fontFamily: "Times-Bold", fontSize: 24 },
  docSubtitle: {
    fontFamily: "Times-Bold",
    fontSize: 10,
    color: ORANGE,
    marginTop: 2,
  },
  sectionTitle: {
    fontFamily: "Times-Bold",
    fontSize: 12,
    marginTop: 20,
    marginBottom: 8,
  },
  infoTable: { borderWidth: 1, borderColor: "#ccc" },
  infoRow: { flexDirection: "row" },
  infoCellLabel: {
    width: "20%",
    backgroundColor: "#FBEAE0",
    fontFamily: "Helvetica-Bold",
    padding: 5,
    borderWidth: 0.5,
    borderColor: "#ccc",
  },
  infoCellValue: {
    width: "30%",
    padding: 5,
    borderWidth: 0.5,
    borderColor: "#ccc",
  },
  paymentTable: { borderWidth: 1, borderColor: "#ccc" },
  paymentHeaderRow: { flexDirection: "row" },
  paymentHeaderCell: {
    width: "25%",
    backgroundColor: DARK,
    color: "#fff",
    fontFamily: "Helvetica-Bold",
    padding: 5,
    borderWidth: 0.5,
    borderColor: "#ccc",
  },
  paymentRow: { flexDirection: "row" },
  paymentCellLabel: {
    width: "25%",
    padding: 5,
    borderWidth: 0.5,
    borderColor: "#ccc",
  },
  paymentCellValue: {
    width: "25%",
    padding: 5,
    borderWidth: 0.5,
    borderColor: "#ccc",
    textAlign: "right",
  },
  netRow: { flexDirection: "row", backgroundColor: "#FBEAE0" },
  netLabelCell: {
    width: "75%",
    padding: 6,
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    borderWidth: 0.5,
    borderColor: ORANGE,
  },
  netValueCell: {
    width: "25%",
    padding: 6,
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    textAlign: "right",
    borderWidth: 0.5,
    borderColor: ORANGE,
  },
  footnote: { fontSize: 7, fontFamily: "Times-Italic", marginTop: 6 },
  signatureRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 50,
  },
  signatureBlock: { width: "45%" },
  signatureLine: {
    borderBottomWidth: 1,
    borderBottomColor: DARK,
    marginTop: 45,
  },
  signatureName: { marginTop: 3 },
  varietyTable: { borderWidth: 1, borderColor: "#ccc", marginTop: 4 },
  varietyHeaderRow: { flexDirection: "row" },
  varietyHeaderCell: {
    backgroundColor: DARK,
    color: "#fff",
    fontFamily: "Helvetica-Bold",
    fontSize: 7,
    padding: 4,
    borderWidth: 0.5,
    borderColor: "#ccc",
    textAlign: "center",
  },
  varietyRow: { flexDirection: "row" },
  varietyCell: {
    fontSize: 7,
    padding: 4,
    borderWidth: 0.5,
    borderColor: "#ccc",
    textAlign: "center",
  },
  varietySubtotalRow: { flexDirection: "row", backgroundColor: "#f3f3f3" },
  varietyGrandRow: { flexDirection: "row", backgroundColor: "#FBEAE0" },
  generatedAt: {
    position: "absolute",
    bottom: 20,
    left: 36,
    fontSize: 7,
    color: GRAY,
  },
});

function Letterhead({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <>
      <View style={styles.headerRow}>
        <View style={styles.logoBox}>
          <Image src={logo} style={styles.logoImage} />
        </View>
        <View style={styles.companyBlock}>
          <Text style={styles.companyName}>{COMPANY_NAME}</Text>
          <Text style={styles.companyLine}>{COMPANY_WEBSITE}</Text>
          <Text style={styles.companyLineItalic}>{COMPANY_ADDRESS}</Text>
        </View>
      </View>
      <View style={styles.divider} />
      <Text style={styles.docTitle}>{title}</Text>
      <Text style={styles.docSubtitle}>{subtitle}</Text>
    </>
  );
}

function adjustmentRows(
  adjustments: PayrollAdjustment[],
  type: "income" | "deduction",
) {
  return adjustments.filter((a) => a.type === type);
}

export function RiderPayrollPdfDocument({
  payroll,
  sales,
}: {
  payroll: RiderPayroll;
  sales: RiderSale[];
}) {
  const sortedSales = [...sales].sort((a, b) => a.date.localeCompare(b.date));

  const dayRows = sortedSales.map((sale) => {
    let totalKopi = 0;
    let totalRoti = 0;
    for (const line of sale.lines) {
      if (line.category === "bakery") totalRoti += line.soldQty;
      else totalKopi += line.soldQty;
    }
    return { sale, totalKopi, totalRoti };
  });

  const subtotalKopi = dayRows.reduce((sum, r) => sum + r.totalKopi, 0);
  const subtotalRoti = dayRows.reduce((sum, r) => sum + r.totalRoti, 0);
  const gajiHarianTotal = payroll.dailyWageRate * payroll.daysWorked;

  const incomeAdjustments = adjustmentRows(payroll.adjustments, "income");
  const deductionAdjustments = adjustmentRows(payroll.adjustments, "deduction");

  // Per-product totals as ROWS, not columns — adding more products grows the
  // table downward instead of squeezing every column, so it scales to any
  // catalog size.
  const productByName = new Map<
    string,
    { name: string; category: string; qty: number; value: number }
  >();
  for (const sale of sortedSales) {
    for (const line of sale.lines) {
      const entry = productByName.get(line.productName) ?? {
        name: line.productName,
        category: line.category,
        qty: 0,
        value: 0,
      };
      entry.qty += line.soldQty;
      entry.value += line.subtotal;
      productByName.set(line.productName, entry);
    }
  }
  const productTotals = [...productByName.values()];
  const generatedAt = formatGeneratedAt();

  return (
    <Document>
      {/* Page 1 — payroll slip */}
      <Page size="A4" style={styles.page}>
        <Letterhead title="PAYROLL RIDERS" subtitle="PT JAM COFFEE ABADI" />

        <Text style={styles.sectionTitle}>Informasi Payroll</Text>
        <View style={styles.infoTable}>
          <View style={styles.infoRow}>
            <Text style={styles.infoCellLabel}>Nama Rider</Text>
            <Text style={styles.infoCellValue}>{payroll.riderName}</Text>
            <Text style={styles.infoCellLabel}>Nomor</Text>
            <Text style={styles.infoCellValue}>
              {payroll.transactionCode ?? "-"}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoCellLabel}>Posisi</Text>
            <Text style={styles.infoCellValue}>Rider</Text>
            <Text style={styles.infoCellLabel}>Tanggal</Text>
            <Text style={styles.infoCellValue}>
              {payroll.paymentDate
                ? formatDateSlashes(payroll.paymentDate)
                : "-"}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoCellLabel}>Hari Kerja</Text>
            <Text style={styles.infoCellValue}>{payroll.daysWorked} hari</Text>
            <Text style={styles.infoCellLabel}>Metode Bayar</Text>
            <Text style={styles.infoCellValue}>
              {RIDER_PAYROLL_PAYMENT_METHOD_LABELS[payroll.paymentMethod]}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoCellLabel}>Periode</Text>
            <Text style={styles.infoCellValue}>
              {formatPeriodLong(payroll.periodStart, payroll.periodEnd)}
            </Text>
            <Text style={styles.infoCellLabel}>Status</Text>
            <Text style={styles.infoCellValue}>
              {RIDER_PAYROLL_STATUS_LABELS[payroll.status]}
            </Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Rincian Pembayaran</Text>
        <View style={styles.paymentTable}>
          <View style={styles.paymentHeaderRow}>
            <Text style={styles.paymentHeaderCell}>Pendapatan</Text>
            <Text style={styles.paymentHeaderCell}>Jumlah</Text>
            <Text style={styles.paymentHeaderCell}>Potongan</Text>
            <Text style={styles.paymentHeaderCell}>Jumlah</Text>
          </View>

          {Array.from({
            length: Math.max(
              3 + incomeAdjustments.length,
              deductionAdjustments.length,
              1,
            ),
          }).map((_, i) => {
            const incomeLabel =
              i === 0
                ? "Gaji Harian"
                : i === 1
                  ? "Pendapatan Penjualan"
                  : i === 2
                    ? "Bonus Target Penjualan"
                    : (incomeAdjustments[i - 3]?.label ?? "");
            const incomeAmount =
              i === 0
                ? gajiHarianTotal
                : i === 1
                  ? payroll.salesIncomeAmount
                  : i === 2
                    ? payroll.bonusAmount
                    : (incomeAdjustments[i - 3]?.amount ?? null);
            const deduction = deductionAdjustments[i];

            return (
              <View style={styles.paymentRow} key={i}>
                <Text style={styles.paymentCellLabel}>{incomeLabel}</Text>
                <Text style={styles.paymentCellValue}>
                  {incomeAmount === null ? "" : formatRp(incomeAmount)}
                </Text>
                <Text style={styles.paymentCellLabel}>
                  {deduction?.label ?? ""}
                </Text>
                <Text style={styles.paymentCellValue}>
                  {deduction ? formatRp(deduction.amount) : ""}
                </Text>
              </View>
            );
          })}

          <View style={styles.paymentRow}>
            <Text
              style={[
                styles.paymentCellLabel,
                { fontFamily: "Helvetica-Bold" },
              ]}
            >
              Total Pendapatan Kotor
            </Text>
            <Text
              style={[
                styles.paymentCellValue,
                { fontFamily: "Helvetica-Bold" },
              ]}
            >
              {formatRp(payroll.grossIncome)}
            </Text>
            <Text
              style={[
                styles.paymentCellLabel,
                { fontFamily: "Helvetica-Bold" },
              ]}
            >
              Total Potongan
            </Text>
            <Text
              style={[
                styles.paymentCellValue,
                { fontFamily: "Helvetica-Bold" },
              ]}
            >
              {formatRp(payroll.totalDeductions)}
            </Text>
          </View>

          <View style={styles.netRow}>
            <Text style={styles.netLabelCell}>Gaji Bersih Diterima</Text>
            <Text style={styles.netValueCell}>
              {formatRp(payroll.netAmount)}
            </Text>
          </View>
        </View>

        <Text style={styles.footnote}>
          *Sistem gaji harian{" "}
          {formatRp(payroll.dailyWageRate).replace("Rp. ", "")}, pendapatan
          penjualan sesuai tarif per unit yang berlaku.
        </Text>

        <View style={styles.signatureRow}>
          <View style={styles.signatureBlock}>
            <Text>Diterima oleh,</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>{payroll.riderName}</Text>
          </View>
          <View style={styles.signatureBlock}>
            <Text>Mengetahui,</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>Direktur Operasional</Text>
          </View>
        </View>

        <Text style={styles.generatedAt} fixed>
          Dicetak pada {generatedAt}
        </Text>
      </Page>

      {/* Page 2 — sales recap */}
      <Page size="A4" style={styles.page}>
        <Letterhead title="REKAP PENJUALAN" subtitle="PT JAM COFFEE ABADI" />

        <Text style={styles.sectionTitle}>Informasi Penjualan</Text>
        <View style={styles.infoTable}>
          <View style={styles.infoRow}>
            <Text
              style={[styles.infoCellLabel, { width: "34%", color: ORANGE }]}
            >
              Nama Rider
            </Text>
            <Text
              style={[styles.infoCellLabel, { width: "33%", color: ORANGE }]}
            >
              Periode
            </Text>
            <Text
              style={[styles.infoCellLabel, { width: "33%", color: ORANGE }]}
            >
              Tanggal
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={[styles.infoCellValue, { width: "34%" }]}>
              {payroll.riderName}
            </Text>
            <Text style={[styles.infoCellValue, { width: "33%" }]}>
              {formatPeriodShort(payroll.periodStart, payroll.periodEnd)}
            </Text>
            <Text style={[styles.infoCellValue, { width: "33%" }]}>
              {payroll.paymentDate
                ? formatDateSlashes(payroll.paymentDate)
                : "-"}
            </Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Rekap Harian</Text>
        <View style={styles.varietyTable}>
          <View style={styles.varietyHeaderRow}>
            <Text style={[styles.varietyHeaderCell, { width: "16%" }]}>
              Tanggal
            </Text>
            <Text style={[styles.varietyHeaderCell, { width: "14%" }]}>
              Total Kopi
            </Text>
            <Text style={[styles.varietyHeaderCell, { width: "14%" }]}>
              Total Roti
            </Text>
            <Text style={[styles.varietyHeaderCell, { width: "22%" }]}>
              Pendapatan Rider
            </Text>
            <Text style={[styles.varietyHeaderCell, { width: "14%" }]}>
              Bonus
            </Text>
            <Text style={[styles.varietyHeaderCell, { width: "20%" }]}>
              Gaji Harian
            </Text>
          </View>

          {dayRows.map(({ sale, totalKopi, totalRoti }) => (
            <View style={styles.varietyRow} key={sale.id}>
              <Text style={[styles.varietyCell, { width: "16%" }]}>
                {formatDateShortSlash(sale.date)}
              </Text>
              <Text style={[styles.varietyCell, { width: "14%" }]}>
                {totalKopi || "-"}
              </Text>
              <Text style={[styles.varietyCell, { width: "14%" }]}>
                {totalRoti || "-"}
              </Text>
              <Text style={[styles.varietyCell, { width: "22%" }]}>
                {formatRp(sale.totalSalesIncome)}
              </Text>
              <Text style={[styles.varietyCell, { width: "14%" }]}>
                {formatRp(sale.totalBonus)}
              </Text>
              <Text style={[styles.varietyCell, { width: "20%" }]}>
                {formatRp(payroll.dailyWageRate)}
              </Text>
            </View>
          ))}

          <View style={styles.varietySubtotalRow}>
            <Text
              style={[
                styles.varietyCell,
                { width: "16%", fontFamily: "Helvetica-Bold" },
              ]}
            >
              SUBTOTAL
            </Text>
            <Text
              style={[
                styles.varietyCell,
                { width: "14%", fontFamily: "Helvetica-Bold" },
              ]}
            >
              {subtotalKopi}
            </Text>
            <Text
              style={[
                styles.varietyCell,
                { width: "14%", fontFamily: "Helvetica-Bold" },
              ]}
            >
              {subtotalRoti}
            </Text>
            <Text
              style={[
                styles.varietyCell,
                { width: "22%", fontFamily: "Helvetica-Bold" },
              ]}
            >
              {formatRp(payroll.salesIncomeAmount)}
            </Text>
            <Text
              style={[
                styles.varietyCell,
                { width: "14%", fontFamily: "Helvetica-Bold" },
              ]}
            >
              {formatRp(payroll.bonusAmount)}
            </Text>
            <Text
              style={[
                styles.varietyCell,
                { width: "20%", fontFamily: "Helvetica-Bold" },
              ]}
            >
              {formatRp(gajiHarianTotal)}
            </Text>
          </View>

          <View style={styles.varietyGrandRow}>
            <Text
              style={[
                styles.varietyCell,
                { width: "80%", fontFamily: "Helvetica-Bold" },
              ]}
            >
              TOTAL PENDAPATAN KESELURUHAN
            </Text>
            <Text
              style={[
                styles.varietyCell,
                { width: "20%", fontFamily: "Helvetica-Bold" },
              ]}
            >
              {formatRp(payroll.grossIncome)}
            </Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Rincian per Varian</Text>
        <View style={styles.varietyTable}>
          <View style={styles.varietyHeaderRow}>
            <Text
              style={[
                styles.varietyHeaderCell,
                { width: "45%", textAlign: "left" },
              ]}
            >
              Varian
            </Text>
            <Text style={[styles.varietyHeaderCell, { width: "20%" }]}>
              Kategori
            </Text>
            <Text style={[styles.varietyHeaderCell, { width: "15%" }]}>
              Qty Terjual
            </Text>
            <Text style={[styles.varietyHeaderCell, { width: "20%" }]}>
              Nilai Penjualan
            </Text>
          </View>

          {productTotals.length === 0 ? (
            <View style={styles.varietyRow}>
              <Text
                style={[
                  styles.varietyCell,
                  { width: "100%", textAlign: "left" },
                ]}
              >
                Tidak ada penjualan tercatat pada periode ini.
              </Text>
            </View>
          ) : (
            productTotals.map((p) => (
              <View style={styles.varietyRow} key={p.name}>
                <Text
                  style={[
                    styles.varietyCell,
                    { width: "45%", textAlign: "left" },
                  ]}
                >
                  {p.name}
                </Text>
                <Text style={[styles.varietyCell, { width: "20%" }]}>
                  {CATEGORY_LABELS[p.category] ?? p.category}
                </Text>
                <Text style={[styles.varietyCell, { width: "15%" }]}>
                  {p.qty}
                </Text>
                <Text style={[styles.varietyCell, { width: "20%" }]}>
                  {formatRp(p.value)}
                </Text>
              </View>
            ))
          )}
        </View>

        <View style={styles.signatureRow}>
          <View style={styles.signatureBlock}>
            <Text>Dicatat oleh,</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>{payroll.riderName}</Text>
          </View>
          <View style={styles.signatureBlock}>
            <Text>Diperiksa oleh,</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>Admin Operasional</Text>
          </View>
        </View>

        <Text style={styles.generatedAt} fixed>
          Dicetak pada {generatedAt}
        </Text>
      </Page>
    </Document>
  );
}
